import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import notificationsAPI from '../services/notifications.api.js';
import { toast } from 'react-hot-toast';

const useNotificationsStore = create(
  persist(
    (set, get) => ({
      // State
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
      
      // Pagination
      pagination: null,

      // =================== NOTIFICATION ACTIONS ===================
      getNotifications: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await notificationsAPI.getNotifications(params);
          
          set({
            notifications: response.data.notifications,
            unreadCount: response.data.unreadCount,
            pagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          console.error('Get notifications error:', message);
          set({ error: message });
          return {
            notifications: [],
            unreadCount: 0,
            pagination: null,
          };
        } finally {
          set({ isLoading: false });
        }
      },

      getUnreadCount: async () => {
        try {
          const response = await notificationsAPI.getUnreadCount();
          
          set({ unreadCount: response.data.count });
          return response.data.count;
        } catch (error) {
          console.error('Get unread count error:', error);
          return 0;
        }
      },

      markAsRead: async (notificationId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await notificationsAPI.markAsRead(notificationId);
          
          // Update local state
          set(state => ({
            notifications: state.notifications.map(notification =>
              notification.id === notificationId
                ? { ...notification, read: true, readAt: new Date().toISOString() }
                : notification
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      markAllAsRead: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await notificationsAPI.markAllAsRead();
          
          // Update all notifications to read
          set(state => ({
            notifications: state.notifications.map(notification => ({
              ...notification,
              read: true,
              readAt: new Date().toISOString()
            })),
            unreadCount: 0,
          }));
          
          toast.success('All notifications marked as read');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteNotification: async (notificationId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await notificationsAPI.deleteNotification(notificationId);
          
          // Remove from local state
          const notificationToDelete = get().notifications.find(n => n.id === notificationId);
          const wasUnread = notificationToDelete?.read === false;
          
          set(state => ({
            notifications: state.notifications.filter(n => n.id !== notificationId),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          }));
          
          toast.success('Notification deleted');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      clearAllNotifications: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await notificationsAPI.clearAllNotifications();
          
          set({
            notifications: [],
            unreadCount: 0,
            pagination: null,
          });
          
          toast.success('All notifications cleared');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Add a new notification (for real-time updates)
      addNotification: (notification) => {
        set(state => ({
          notifications: [notification, ...state.notifications],
          unreadCount: state.unreadCount + 1,
        }));
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'notifications-storage',
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 20), // Cache only recent notifications
        unreadCount: state.unreadCount,
      }),
    }
  )
);

export default useNotificationsStore;